import { Select, Space } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'

export interface Ward {
    name: string
    code: number
    codename?: string
    division_type?: string
    short_codename?: string
}

export interface District {
    name: string
    code: number
    codename?: string
    division_type?: string
    short_codename?: string
    wards: Ward[]
}

export interface Province {
    name: string
    code: number
    codename?: string
    division_type?: string
    phone_code?: number
    districts: District[]
}

const CACHE_KEY = 'vn_address_v1'
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000
const API_URL = 'https://provinces.open-api.vn/api/?depth=3'

let memoryCache: Province[] | null = null
let inflight: Promise<Province[]> | null = null

async function loadProvinces(): Promise<Province[]> {
    if (memoryCache) return memoryCache

    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed?.ts && Date.now() - parsed.ts < CACHE_TTL_MS && Array.isArray(parsed.data)) {
                memoryCache = parsed.data as Province[]
                return memoryCache
            }
        }
    } catch {
        // corrupt cache — ignore, refetch
    }

    if (inflight) return inflight

    inflight = (async () => {
        try {
            const resp = await fetch(API_URL)
            if (!resp.ok) throw new Error(`status ${resp.status}`)
            const data: Province[] = await resp.json()
            memoryCache = data
            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }))
            } catch {
                // quota exceeded — memory cache still holds it for the session
            }
            return data
        } catch (err) {
            console.warn('[VNAddressSelect] load failed', err)
            memoryCache = []
            return memoryCache
        } finally {
            inflight = null
        }
    })()
    return inflight
}

export const VN_REGIONS = [
    'Đông Bắc Bộ',
    'Tây Bắc Bộ',
    'Đồng bằng sông Hồng',
    'Bắc Trung Bộ',
    'Duyên hải Nam Trung Bộ',
    'Tây Nguyên',
    'Đông Nam Bộ',
    'Đồng bằng sông Cửu Long',
] as const

// Best-effort auto-fill; user can always override.
const PROVINCE_TO_REGION: Record<string, string> = {
    'Thành phố Hà Nội': 'Đồng bằng sông Hồng',
    'Thành phố Hồ Chí Minh': 'Đông Nam Bộ',
    'Thành phố Hải Phòng': 'Đồng bằng sông Hồng',
    'Thành phố Đà Nẵng': 'Duyên hải Nam Trung Bộ',
    'Thành phố Cần Thơ': 'Đồng bằng sông Cửu Long',
    'Tỉnh Hà Giang': 'Đông Bắc Bộ',
    'Tỉnh Cao Bằng': 'Đông Bắc Bộ',
    'Tỉnh Bắc Kạn': 'Đông Bắc Bộ',
    'Tỉnh Tuyên Quang': 'Đông Bắc Bộ',
    'Tỉnh Lào Cai': 'Tây Bắc Bộ',
    'Tỉnh Điện Biên': 'Tây Bắc Bộ',
    'Tỉnh Lai Châu': 'Tây Bắc Bộ',
    'Tỉnh Sơn La': 'Tây Bắc Bộ',
    'Tỉnh Yên Bái': 'Tây Bắc Bộ',
    'Tỉnh Hoà Bình': 'Tây Bắc Bộ',
    'Tỉnh Thái Nguyên': 'Đông Bắc Bộ',
    'Tỉnh Lạng Sơn': 'Đông Bắc Bộ',
    'Tỉnh Quảng Ninh': 'Đông Bắc Bộ',
    'Tỉnh Bắc Giang': 'Đông Bắc Bộ',
    'Tỉnh Phú Thọ': 'Đông Bắc Bộ',
    'Tỉnh Vĩnh Phúc': 'Đồng bằng sông Hồng',
    'Tỉnh Bắc Ninh': 'Đồng bằng sông Hồng',
    'Tỉnh Hải Dương': 'Đồng bằng sông Hồng',
    'Tỉnh Hưng Yên': 'Đồng bằng sông Hồng',
    'Tỉnh Thái Bình': 'Đồng bằng sông Hồng',
    'Tỉnh Hà Nam': 'Đồng bằng sông Hồng',
    'Tỉnh Nam Định': 'Đồng bằng sông Hồng',
    'Tỉnh Ninh Bình': 'Đồng bằng sông Hồng',
    'Tỉnh Thanh Hóa': 'Bắc Trung Bộ',
    'Tỉnh Nghệ An': 'Bắc Trung Bộ',
    'Tỉnh Hà Tĩnh': 'Bắc Trung Bộ',
    'Tỉnh Quảng Bình': 'Bắc Trung Bộ',
    'Tỉnh Quảng Trị': 'Bắc Trung Bộ',
    'Tỉnh Thừa Thiên Huế': 'Bắc Trung Bộ',
    'Tỉnh Quảng Nam': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Quảng Ngãi': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Bình Định': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Phú Yên': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Khánh Hòa': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Ninh Thuận': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Bình Thuận': 'Duyên hải Nam Trung Bộ',
    'Tỉnh Kon Tum': 'Tây Nguyên',
    'Tỉnh Gia Lai': 'Tây Nguyên',
    'Tỉnh Đắk Lắk': 'Tây Nguyên',
    'Tỉnh Đắk Nông': 'Tây Nguyên',
    'Tỉnh Lâm Đồng': 'Tây Nguyên',
    'Tỉnh Bình Phước': 'Đông Nam Bộ',
    'Tỉnh Tây Ninh': 'Đông Nam Bộ',
    'Tỉnh Bình Dương': 'Đông Nam Bộ',
    'Tỉnh Đồng Nai': 'Đông Nam Bộ',
    'Tỉnh Bà Rịa - Vũng Tàu': 'Đông Nam Bộ',
    'Tỉnh Long An': 'Đồng bằng sông Cửu Long',
    'Tỉnh Tiền Giang': 'Đồng bằng sông Cửu Long',
    'Tỉnh Bến Tre': 'Đồng bằng sông Cửu Long',
    'Tỉnh Trà Vinh': 'Đồng bằng sông Cửu Long',
    'Tỉnh Vĩnh Long': 'Đồng bằng sông Cửu Long',
    'Tỉnh Đồng Tháp': 'Đồng bằng sông Cửu Long',
    'Tỉnh An Giang': 'Đồng bằng sông Cửu Long',
    'Tỉnh Kiên Giang': 'Đồng bằng sông Cửu Long',
    'Tỉnh Hậu Giang': 'Đồng bằng sông Cửu Long',
    'Tỉnh Sóc Trăng': 'Đồng bằng sông Cửu Long',
    'Tỉnh Bạc Liêu': 'Đồng bằng sông Cửu Long',
    'Tỉnh Cà Mau': 'Đồng bằng sông Cửu Long',
}

const NAME_PREFIXES = [
    'Thành phố ',
    'Tỉnh ',
    'Quận ',
    'Huyện ',
    'Thị xã ',
    'Phường ',
    'Xã ',
    'Thị trấn ',
]

function stripPrefix(s: string): string {
    for (const p of NAME_PREFIXES) {
        if (s.startsWith(p)) return s.slice(p.length)
    }
    return s
}

function resolveName(input: string | undefined, options: { name: string }[]): string | undefined {
    if (!input) return undefined
    if (options.some(o => o.name === input)) return input
    const inputBare = stripPrefix(input).toLowerCase()
    const match = options.find(o => stripPrefix(o.name).toLowerCase() === inputBare)
    if (match) return match.name
    const loose = options.find(o => o.name.toLowerCase().includes(input.toLowerCase()) || input.toLowerCase().includes(o.name.toLowerCase()))
    return loose?.name
}

interface Props {
    region?: string
    province?: string
    district?: string
    ward?: string
    onChange?: (values: { region?: string; province?: string; district?: string; ward?: string }) => void
    style?: React.CSSProperties
}

export default function VNAddressSelect({ region, province, district, ward, onChange, style }: Props) {
    const [data, setData] = useState<Province[]>([])
    const [loading, setLoading] = useState(false)
    // Track whether the user has explicitly picked a region — protects against auto-fill overwriting a manual choice.
    const regionManuallySetRef = useRef(false)
    // Track legacy-value resolution so we only reconcile once per initial mount.
    const resolvedInitialRef = useRef(false)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        loadProvinces().then(list => {
            if (cancelled) return
            setData(list)
            setLoading(false)
        })
        return () => { cancelled = true }
    }, [])

    // Once data arrives, reconcile initial province/district/ward if they are legacy short-form.
    useEffect(() => {
        if (resolvedInitialRef.current) return
        if (data.length === 0) return
        if (!province && !district && !ward) {
            resolvedInitialRef.current = true
            return
        }

        const resolvedProvince = resolveName(province, data)
        if (!resolvedProvince) {
            if (province) console.warn('[VNAddressSelect] cannot match province', province)
            resolvedInitialRef.current = true
            return
        }

        const provObj = data.find(p => p.name === resolvedProvince)
        const resolvedDistrict = resolveName(district, provObj?.districts ?? [])
        const distObj = provObj?.districts.find(d => d.name === resolvedDistrict)
        const resolvedWard = resolveName(ward, distObj?.wards ?? [])

        if (resolvedProvince !== province || resolvedDistrict !== district || resolvedWard !== ward) {
            onChange?.({
                region: region || PROVINCE_TO_REGION[resolvedProvince] || undefined,
                province: resolvedProvince,
                district: resolvedDistrict,
                ward: resolvedWard,
            })
        }
        resolvedInitialRef.current = true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const provinceOptions = useMemo(
        () => data.map(p => ({ label: p.name, value: p.name })),
        [data],
    )

    const districtOptions = useMemo(() => {
        if (!province) return []
        const p = data.find(x => x.name === province)
        return (p?.districts ?? []).map(d => ({ label: d.name, value: d.name }))
    }, [data, province])

    const wardOptions = useMemo(() => {
        if (!province || !district) return []
        const p = data.find(x => x.name === province)
        const d = p?.districts.find(x => x.name === district)
        return (d?.wards ?? []).map(w => ({ label: w.name, value: w.name }))
    }, [data, province, district])

    const regionOptions = useMemo(
        () => VN_REGIONS.map(r => ({ label: r, value: r })),
        [],
    )

    const emit = (next: { region?: string; province?: string; district?: string; ward?: string }) => {
        onChange?.(next)
    }

    const handleRegion = (v?: string) => {
        regionManuallySetRef.current = true
        emit({ region: v, province, district, ward })
    }

    const handleProvince = (v?: string) => {
        // Reset downstream when province changes so stale district/ward can't leak into submit.
        const autoRegion = !regionManuallySetRef.current && v ? PROVINCE_TO_REGION[v] : undefined
        emit({
            region: autoRegion ?? region,
            province: v,
            district: undefined,
            ward: undefined,
        })
    }

    const handleDistrict = (v?: string) => {
        emit({ region, province, district: v, ward: undefined })
    }

    const handleWard = (v?: string) => {
        emit({ region, province, district, ward: v })
    }

    const filterOption = (input: string, option?: { label: string; value: string }) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

    return (
        <Space style={{ display: 'flex', width: '100%', ...style }} size='middle' align='start'>
            <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ marginBottom: 8 }}>Vùng</div>
                <Select
                    value={region}
                    options={regionOptions}
                    onChange={handleRegion}
                    placeholder='Chọn Vùng'
                    showSearch
                    allowClear
                    filterOption={filterOption}
                    style={{ width: '100%' }}
                />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ marginBottom: 8 }}>Tỉnh/TP</div>
                <Select
                    value={province}
                    options={provinceOptions}
                    onChange={handleProvince}
                    placeholder='Chọn Tỉnh/TP'
                    showSearch
                    allowClear
                    filterOption={filterOption}
                    loading={loading}
                    style={{ width: '100%' }}
                />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ marginBottom: 8 }}>Quận/Huyện</div>
                <Select
                    value={district}
                    options={districtOptions}
                    onChange={handleDistrict}
                    placeholder='Chọn Quận/Huyện'
                    showSearch
                    allowClear
                    disabled={!province}
                    filterOption={filterOption}
                    style={{ width: '100%' }}
                />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ marginBottom: 8 }}>Phường/Xã</div>
                <Select
                    value={ward}
                    options={wardOptions}
                    onChange={handleWard}
                    placeholder='Chọn Phường/Xã'
                    showSearch
                    allowClear
                    disabled={!district}
                    filterOption={filterOption}
                    style={{ width: '100%' }}
                />
            </div>
        </Space>
    )
}
